import React, { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCallInProgress,
  setActiveCallContact,
  setCallType,
} from "../../features/channelSlice";
import { Phone, Video } from "lucide-react";
import { getColorForLetter } from "../../utils/colorUtils";
import { sendCaraStart } from "../../utils/caraBridge";

// Component for the contact list when no call is active
const ContactListView = () => {
  const dispatch = useDispatch();
  const { channelConfig, currentChannel } = useSelector(
    (state) => state.channel,
  );
  const contacts = channelConfig[currentChannel]?.contacts || [];
  const contactListRef = useRef(null);

  const handleInitiateCall = (contact, type = "voice") => {
    if (type === "video") {
      sendCaraStart();
    }

    dispatch(setActiveCallContact(contact));
    dispatch(setCallType(type));
    dispatch(setCallInProgress(true));
  };

  // Drag-to-scroll logic
  const isDragging = useRef(false);
  const startY = useRef(0);
  const scrollTop = useRef(0);

  const handleMouseDown = (e) => {
    if (contactListRef.current && contactListRef.current.scrollTop > 0) {
      e.preventDefault();
    }
    isDragging.current = true;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    startY.current = y;
    scrollTop.current = contactListRef.current.scrollTop;
    if (contactListRef.current) {
      contactListRef.current.style.cursor = "grabbing";
      contactListRef.current.style.userSelect = "none";
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || !contactListRef.current) return;
    e.preventDefault();
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const walk = y - startY.current;
    contactListRef.current.scrollTop = scrollTop.current - walk;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (contactListRef.current) {
      contactListRef.current.style.cursor = "grab";
      contactListRef.current.style.userSelect = "auto";
    }
  };

  useEffect(() => {
    const scrollable = contactListRef.current;
    if (scrollable) {
      scrollable.addEventListener("mousedown", handleMouseDown);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      scrollable.addEventListener("touchstart", handleMouseDown, {
        passive: true,
      });
      window.addEventListener("touchmove", handleMouseMove);
      window.addEventListener("touchend", handleMouseUp);
    }

    return () => {
      if (scrollable) {
        scrollable.removeEventListener("mousedown", handleMouseDown);
      }
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, []);

  return (
    <div className="bg-white h-full flex flex-col font-inter">
      <div className="flex items-center justify-center p-4 border-b relative">
        <h1 className="font-semibold text-lg">Contacts</h1>
      </div>
      <div
        ref={contactListRef}
        className="flex-1 overflow-y-auto no-scrollbar"
        style={{
          cursor: "grab",
          userSelect: "auto",
          scrollbarWidth: "none" /* For Firefox */,
        }}
      >
        <ul>
          {contacts.map((contact, index) => (
            <li
              key={index}
              className={`flex items-center justify-between px-4 py-3 border-b hover:bg-gray-50 transition-colors cursor-pointer ${index === 0 ? "contact-list-row" : ""}`}
              onClick={() => handleInitiateCall(contact, "voice")}
              data-contact-index={index}
            >
              <div className="flex items-center min-w-0">
                <div
                  className="w-10 h-10 rounded-full mr-4 flex items-center justify-center text-lg font-bold text-white flex-shrink-0"
                  style={{
                    backgroundColor: getColorForLetter(contact.name.charAt(0)),
                  }}
                >
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate text-base">
                    {contact.name}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {contact.number}
                  </p>
                </div>
              </div>
              <div className="flex space-x-4 pl-4">
                <Phone
                  className="contact-action-icon w-6 h-6 text-blue-500 hover:text-blue-700 cursor-pointer"
                  data-action="voice"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInitiateCall(contact, "voice");
                  }}
                />
                <Video
                  className="contact-action-icon w-6 h-6 text-green-500 hover:text-green-700 cursor-pointer"
                  data-action="video"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInitiateCall(contact, "video");
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ContactListView;
